import { useCallback, useEffect, useRef } from "react";
import { useDictation } from "@/desktop/use-dictation";
import {
  voiceTokens,
  type DictatedSnapshot,
} from "./dictated-word-substitution";

interface LiveDictationSegment {
  /** Draft text from before this recording's speech. */
  base: string;
  /** Draft text as last written with the live transcript. */
  applied: string;
  /** Where the live transcript starts in `applied`; it runs to the end. */
  start: number;
  /** The user edited the draft while listening. */
  userOverrode: boolean;
}

function withTranscript(base: string, transcript: string): string {
  return base.trim() ? `${base.trimEnd()} ${transcript}` : transcript;
}

function appendFinalTranscript(
  current: string,
  transcript: string,
): { text: string; insertAt: number } {
  const text = withTranscript(current, transcript);
  return { text, insertAt: text.length - transcript.length };
}

/** Joins two pieces of draft text with a space unless one is already there. */
function joinWords(left: string, right: string): string {
  if (!left || !right || /\s$/.test(left) || /^\s/.test(right)) {
    return left + right;
  }
  return `${left} ${right}`;
}

/** Index of the occurrence of `needle` in `text` closest to `near`, or -1. */
function nearestOccurrence(text: string, needle: string, near: number): number {
  let best = -1;
  for (
    let at = text.indexOf(needle);
    at >= 0;
    at = text.indexOf(needle, at + 1)
  ) {
    if (best < 0 || Math.abs(at - near) < Math.abs(best - near)) best = at;
  }
  return best;
}

/** Puts the final transcript where the last live transcript sits in `current`,
 *  keeping everything the user typed around or inside it. Returns the draft
 *  and where the final transcript starts in it. */
function placeFinalTranscript(
  { applied, start }: LiveDictationSegment,
  current: string,
  transcript: string,
): { text: string; insertAt: number } {
  // The user's edits, seen as one region between a shared prefix and suffix.
  const limit = Math.min(applied.length, current.length);
  let prefix = 0;
  while (prefix < limit && applied[prefix] === current[prefix]) prefix += 1;
  let suffix = 0;
  while (
    suffix < limit - prefix &&
    applied[applied.length - 1 - suffix] ===
      current[current.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  const live = applied.slice(start);
  const shift =
    applied.length - suffix <= start ? current.length - applied.length : 0;
  const found = nearestOccurrence(current, live, start + shift);
  if (found >= 0) {
    return {
      text:
        current.slice(0, found) +
        transcript +
        current.slice(found + live.length),
      insertAt: found,
    };
  }
  // The user edited inside the live text. What still matches it around the
  // edit is stale and dropped; what they typed stays, after the final
  // transcript unless their edit began before the live text.
  const typed = current.slice(prefix, current.length - suffix);
  const left =
    prefix >= start
      ? applied.slice(0, start)
      : applied.slice(0, prefix) + typed;
  const withFinal = joinWords(left, transcript);
  return {
    text: prefix >= start ? joinWords(withFinal, typed.trim()) : withFinal,
    insertAt: withFinal.length - transcript.length,
  };
}

/** Mirrors interim speech into a controlled draft while dictating. The live
 *  segment is tracked so it can later be cleared after a voice send, or
 *  replaced by the final transcript, without touching text typed before. */
export function useLiveDictationDraft({
  enabled,
  value,
  onChange,
  targetId,
}: {
  enabled: boolean;
  value: string;
  onChange: (value: string) => void;
  /** Comment dictation target this draft belongs to, if any. */
  targetId?: string;
}) {
  const status = useDictation((state) => state.status);
  // Idle drafts skip the per-hypothesis re-render: a canvas holds many bars.
  const partialText = useDictation((state) =>
    enabled ? state.partialText : "",
  );
  const previousStatusRef = useRef(status);
  const segmentRef = useRef<LiveDictationSegment | null>(null);
  const latestValueRef = useRef(value);
  latestValueRef.current = value;
  // Latest interim transcript received while the webview was backgrounded.
  // Rewriting the controlled composer per hidden chunk queues invisible
  // updates that reconcile in one burst on foreground; instead we stash and
  // apply once on return.
  const pendingHiddenTranscriptRef = useRef<string | null>(null);

  const applyLiveTranscript = useCallback(
    (transcript: string): void => {
      // Speech claimed by a composer or comment goes there, not here. The
      // owner is read from the mirrored store, so a claim made in another
      // display's window holds here too.
      const { ownerId } = useDictation.getState();
      if (ownerId && ownerId !== targetId) return;
      const current = segmentRef.current;
      if (current?.userOverrode) return;
      const latest = latestValueRef.current;
      if (current && current.applied !== latest) {
        // The user took over the draft while we were listening. Do not
        // overwrite their typed or pasted edit with another hypothesis.
        segmentRef.current = { ...current, userOverrode: true };
        return;
      }
      const base = current?.base ?? latest;
      const next = withTranscript(base, transcript);
      segmentRef.current = {
        base,
        applied: next,
        start: next.length - transcript.length,
        userOverrode: false,
      };
      if (latest !== next) onChange(next);
    },
    [onChange, targetId],
  );

  useEffect(() => {
    const wasRecording = previousStatusRef.current === "recording";
    previousStatusRef.current = status;
    if (status === "recording" && !wasRecording) {
      // A cancelled or rejected voice message remains available for retry.
      // Make that visible text the baseline for the next recording session.
      segmentRef.current = null;
      pendingHiddenTranscriptRef.current = null;
    }
    if (!enabled) return;
    const transcript = partialText.trim();
    if (status !== "recording" || !transcript) return;
    if (typeof document !== "undefined" && document.hidden) {
      pendingHiddenTranscriptRef.current = transcript;
      return;
    }
    pendingHiddenTranscriptRef.current = null;
    applyLiveTranscript(transcript);
  }, [
    enabled,
    onChange,
    partialText,
    status,
    targetId,
    value,
    applyLiveTranscript,
  ]);

  // Foreground catch-up: apply the latest stashed transcript in a single
  // rewrite instead of the queued per-chunk burst.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVisibilityChange = () => {
      if (document.hidden) return;
      const transcript = pendingHiddenTranscriptRef.current;
      pendingHiddenTranscriptRef.current = null;
      if (!transcript || !enabled) return;
      if (useDictation.getState().status !== "recording") return;
      applyLiveTranscript(transcript);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [enabled, applyLiveTranscript]);

  /** Clears a voice draft that was sent, unless the user edited it. */
  const discardAcceptedDraft = useCallback(() => {
    const segment = segmentRef.current;
    if (
      !segment ||
      segment.userOverrode ||
      latestValueRef.current !== segment.applied
    ) {
      return;
    }
    segmentRef.current = null;
    onChange("");
  }, [onChange]);

  /** Replaces the live preview with the final transcript and keeps it in the
   *  draft, wherever the user's edits while listening moved it; their typed
   *  text is kept. Without a live preview the transcript is appended. Returns
   *  the draft and the transcript's token range in it. */
  const commitFinalTranscript = useCallback(
    (transcript: string): DictatedSnapshot => {
      const segment = segmentRef.current;
      const latest = latestValueRef.current;
      segmentRef.current = null;
      const { text, insertAt } = segment
        ? placeFinalTranscript(segment, latest, transcript)
        : appendFinalTranscript(latest, transcript);
      if (text !== latest) onChange(text);
      const dictatedFrom = voiceTokens(text.slice(0, insertAt)).length;
      return {
        text,
        dictatedFrom,
        dictatedTo: dictatedFrom + voiceTokens(transcript).length,
      };
    },
    [onChange],
  );

  /** True when the user edited the draft during or after the live transcript:
   *  an override while listening, or any change since the last applied partial. */
  const wasDraftEdited = useCallback(() => {
    const segment = segmentRef.current;
    return (
      segment !== null &&
      (segment.userOverrode || latestValueRef.current !== segment.applied)
    );
  }, []);

  /** True when there is text ahead of the transcript in this recording's
   *  segment — typed before the mic was clicked, or typed after the click
   *  but before the first partial arrived (both become the segment's
   *  `base`). Absent a segment yet, falls back to whatever is in the box.
   *  A voice send should still wait for review rather than fire outright
   *  when this is true, the same as when the user visibly edited the draft. */
  const hasBaseText = useCallback(() => {
    const segment = segmentRef.current;
    if (segment) return segment.base.trim().length > 0;
    return latestValueRef.current.trim().length > 0;
  }, []);

  return {
    discardAcceptedDraft,
    commitFinalTranscript,
    wasDraftEdited,
    hasBaseText,
  };
}
